import { useState, useEffect } from 'react';
import { Card, Input, Button, Switch, Badge } from '@getpay93/ui';
import { useOrg, useUpdateOrg, useReminderSettings, useUpdateReminders, useProviders, useConnectProvider } from '../../data/hooks';
import { useAuth } from '../../auth/AuthContext';
import { API_BASE_URL } from '../../lib/env';

type Tab = 'negocio' | 'recordatorios' | 'pagos' | 'cuenta';
const TABS: { id: Tab; label: string }[] = [
  { id: 'negocio', label: 'Negocio' },
  { id: 'recordatorios', label: 'Recordatorios' },
  { id: 'pagos', label: 'Pagos en línea' },
  { id: 'cuenta', label: 'Cuenta' },
];

export function Configuracion() {
  const [tab, setTab] = useState<Tab>('negocio');
  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: tab === t.id ? 'var(--brand-green-soft)' : 'transparent', color: tab === t.id ? 'var(--brand-green)' : 'var(--text-faint)', fontWeight: tab === t.id ? 700 : 600, fontFamily: 'var(--font-sans)' }}>{t.label}</button>
        ))}
      </nav>
      <div style={{ flex: 1 }}>
        {tab === 'negocio' ? <NegocioTab /> : null}
        {tab === 'recordatorios' ? <RecordatoriosTab /> : null}
        {tab === 'pagos' ? <PagosTab /> : null}
        {tab === 'cuenta' ? <CuentaTab /> : null}
      </div>
    </div>
  );
}

function NegocioTab() {
  const org = useOrg();
  const update = useUpdateOrg();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  useEffect(() => { if (org.data) { setName(org.data.name); setTimezone(org.data.timezone); } }, [org.data]);
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
        <Input label="Nombre del negocio" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <Input label="Zona horaria" value={timezone} onChange={(e) => setTimezone(e.currentTarget.value)} />
        <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>Tipo: {org.data?.type ?? '—'} · País: {org.data?.country ?? '—'} · Moneda: {org.data?.currency ?? '—'}</div>
        <Button disabled={update.isPending} onClick={() => update.mutate({ name, timezone })}>{update.isPending ? 'Guardando…' : 'Guardar'}</Button>
      </div>
    </Card>
  );
}

function RecordatoriosTab() {
  const rem = useReminderSettings();
  const update = useUpdateReminders();
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState(false);
  const [wa, setWa] = useState(false);
  const [before, setBefore] = useState('3');
  const [after, setAfter] = useState('1');
  const [msg, setMsg] = useState('');
  useEffect(() => { const d = rem.data; if (d) { setEnabled(!!d.enabled); setEmail(!!d.enableEmail); setWa(!!d.enableWhatsapp); setBefore(String(d.daysBeforeDue ?? 3)); setAfter(String(d.daysAfterDue ?? 1)); setMsg(d.customMessage ?? ''); } }, [rem.data]);
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 460 }}>
        <Switch checked={enabled} onChange={setEnabled} label="Recordatorios activados" />
        <Switch checked={email} onChange={setEmail} label="Por email" />
        <Switch checked={wa} onChange={setWa} label="Por WhatsApp" />
        <Input label="Días antes del vencimiento" type="number" value={before} onChange={(e) => setBefore(e.currentTarget.value)} />
        <Input label="Días después del vencimiento" type="number" value={after} onChange={(e) => setAfter(e.currentTarget.value)} />
        <Input label="Mensaje personalizado" value={msg} onChange={(e) => setMsg(e.currentTarget.value)} />
        <Button disabled={update.isPending} onClick={() => update.mutate({ enabled, enableEmail: email, enableWhatsapp: wa, daysBeforeDue: Number(before), daysAfterDue: Number(after), customMessage: msg })}>{update.isPending ? 'Guardando…' : 'Guardar'}</Button>
      </div>
    </Card>
  );
}

function PagosTab() {
  const providers = useProviders();
  const connect = useConnectProvider();
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const deuna = (providers.data ?? []).find((p) => p.provider.toLowerCase() === 'deuna');
  const webhookUrl = `${API_BASE_URL.replace(/\/+$/, '')}/v1/webhooks/deuna`;
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ color: 'var(--text-strong)' }}>DeUna</strong>
          {deuna?.isActive ? <Badge tone="paid">Conectado</Badge> : <Badge tone="neutral">No conectado</Badge>}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Conecta DeUna para recibir pagos con link. El secreto no se vuelve a mostrar por seguridad.</p>
        <Input label="API Key" placeholder="API key de DeUna" value={apiKey} onChange={(e) => setApiKey(e.currentTarget.value)} />
        <Input label="Webhook secret (opcional)" placeholder="secret para validar el webhook" value={secret} onChange={(e) => setSecret(e.currentTarget.value)} />
        <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Configura esta URL de webhook en DeUna:<br /><code style={{ color: 'var(--text-body)' }}>{webhookUrl}</code></div>
        <Button disabled={!apiKey || connect.isPending} onClick={() => connect.mutate({ provider: 'DeUna', apiKey, webhookSecret: secret || undefined }, { onSuccess: () => { setApiKey(''); setSecret(''); } })}>{connect.isPending ? 'Conectando…' : 'Conectar'}</Button>
      </div>
    </Card>
  );
}

function CuentaTab() {
  const { logout, session } = useAuth();
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
        <div><div style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{session?.user.name}</div><div style={{ color: 'var(--text-hint)', fontSize: 14 }}>{session?.user.email}</div></div>
        <Button variant="outline" onClick={logout}>Cerrar sesión</Button>
      </div>
    </Card>
  );
}
